import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { FaSearch, FaFilter, FaFileDownload, FaUser, FaBuilding, FaCheckCircle, FaClipboardList } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';

const EngineerWorkAllocation = () => {
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [selectedMeeting, setSelectedMeeting] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // Fetch data from both potential user nodes
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {};
        let socData = {};
        let loadedNodes = 0;

        const processData = () => {
            if (loadedNodes < 2) return;

            const allAllocations = [];

            // Process Engineers
            Object.keys(engData).forEach(key => {
                const user = engData[key];
                if (user.work_payment) {
                    Object.keys(user.work_payment).forEach(meetingNum => {
                        const payment = user.work_payment[meetingNum];
                        allAllocations.push({
                            id: `${key}-${meetingNum}`,
                            userName: user.firstName + ' ' + (user.surname || ''),
                            mobile: user.whatsappNo,
                            type: 'Individual Engineer',
                            meetingNumber: meetingNum,
                            selectedWorks: payment.selectedWorks || [],
                            receiptId: payment.receiptId,
                            date: payment.date,
                            status: payment.status
                        });
                    });
                }
            });

            // Process Societies
            Object.keys(socData).forEach(key => {
                const user = socData[key];
                if (user.work_payment) {
                    Object.keys(user.work_payment).forEach(meetingNum => {
                        const payment = user.work_payment[meetingNum];
                        allAllocations.push({
                            id: `${key}-${meetingNum}`,
                            userName: user.societyName,
                            mobile: user.chairmanWhatsapp,
                            type: 'Society',
                            meetingNumber: meetingNum,
                            selectedWorks: payment.selectedWorks || [],
                            receiptId: payment.receiptId,
                            date: payment.date,
                            status: payment.status
                        });
                    });
                }
            });

            // Sort by date (newest first)
            setAllocations(allAllocations.sort((a, b) => new Date(b.date) - new Date(a.date)));
            setLoading(false);
        };

        const unsubEng = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            loadedNodes++;
            processData();
        });

        const unsubSoc = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            loadedNodes++;
            processData();
        });

        return () => {
            unsubscribeAuth();
            unsubEng();
            unsubSoc();
        };
    }, [navigate]);

    const filteredData = allocations.filter(item => {
        const matchesSearch = item.userName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             item.mobile.includes(searchTerm) ||
                             item.receiptId?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || 
                           (filterType === 'engineer' && item.type === 'Individual Engineer') ||
                           (filterType === 'society' && item.type === 'Society');
        return matchesSearch && matchesType;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-12 w-12 text-blue-600" />
            </div>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-8 bg-slate-50 min-h-screen">
                <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">काम वाटप (Work Allocation)</h1>
                        <p className="text-gray-500 font-bold mt-1 uppercase text-xs tracking-widest">निवडलेल्या कामांची यादी (List of Selected Works)</p>
                    </div>
                    <button className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition shadow-lg shadow-green-100 font-bold">
                        <FaFileDownload />
                        <span>Excel डाउनलोड</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mb-8 flex flex-col md:flex-row gap-6">
                    <div className="flex-grow relative">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="शोध (नाव, मोबाईल किंवा पावती क्रमांक)..." 
                            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-100 focus:ring-4 focus:ring-blue-50 focus:border-blue-500 outline-none transition-all font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center space-x-3">
                        <FaFilter className="text-gray-400" />
                        <select 
                            className="bg-gray-50 border-none rounded-2xl px-6 py-3 outline-none focus:ring-4 focus:ring-blue-50 font-bold text-gray-700 cursor-pointer"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="all">सर्व प्रकार (All Types)</option>
                            <option value="engineer">अभियंता (Engineer)</option>
                            <option value="society">संस्था (Society)</option>
                        </select>
                    </div>
                </div>

                {/* Meeting Filter Buttons */}
                <div className="flex flex-wrap items-center gap-3 mb-10 px-4">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mr-2 flex items-center gap-2">
                        <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                        बैठक निवडा (Select Meeting):
                    </span>
                    <button 
                        onClick={() => setSelectedMeeting('all')}
                        className={`px-6 py-2.5 rounded-2xl font-bold text-xs transition-all border-2 flex items-center gap-2 active:scale-95 ${
                            selectedMeeting === 'all' 
                            ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-100' 
                            : 'bg-white border-gray-100 text-gray-500 hover:border-blue-200'
                        }`}
                    >
                        सर्व (ALL)
                    </button>
                    {[...new Set(allocations.map(a => a.meetingNumber))].sort((a,b) => b-a).map(mNum => (
                        <button 
                            key={mNum}
                            onClick={() => setSelectedMeeting(mNum)}
                            className={`px-6 py-2.5 rounded-2xl font-bold text-xs transition-all border-2 flex items-center gap-2 active:scale-95 ${
                                selectedMeeting === mNum 
                                ? 'bg-[#1a3c34] border-[#1a3c34] text-white shadow-xl shadow-gray-200' 
                                : 'bg-white border-gray-100 text-gray-500 hover:border-[#1a3c34]/20'
                            }`}
                        >
                            <span className="opacity-60">बैठक</span> {mNum}
                        </button>
                    ))}
                </div>

                <div className="space-y-12 pb-20">
                    {Object.entries(
                        filteredData.reduce((acc, item) => {
                            const meetingNum = item.meetingNumber || "Unknown";
                            // Filter by selected meeting
                            if (selectedMeeting !== 'all' && meetingNum !== selectedMeeting) return acc;
                            
                            if (!acc[meetingNum]) acc[meetingNum] = [];
                            acc[meetingNum].push(item);
                            return acc;
                        }, {})
                    )
                    .sort(([a], [b]) => b - a) // Sort meetings descending
                    .map(([meetingNum, meetingAllocations]) => (
                        <div key={meetingNum} className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                            {/* Meeting Header */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 px-4">
                                <div className="flex items-center gap-4 flex-grow">
                                    <div className="h-0.5 flex-grow bg-gradient-to-r from-transparent via-blue-200 to-transparent"></div>
                                    <div className="bg-[#1a3c34] text-white px-8 py-3 rounded-full font-bold text-sm shadow-xl shadow-gray-200 border-2 border-white flex items-center gap-3 whitespace-nowrap">
                                        <span className="hidden md:inline">काम वाटप समिती</span>
                                        <span>बैठक क्रं.</span>
                                        <span className="text-xl bg-white/20 px-3 py-1 rounded-lg">{meetingNum}</span>
                                    </div>
                                    <div className="h-0.5 flex-grow bg-gradient-to-l from-transparent via-blue-200 to-transparent"></div>
                                </div>
                            </div>

                            <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-900 text-white">
                                                <th className="px-8 py-6 text-[10px] font-bold uppercase tracking-[0.2em] border-b border-gray-800">वापरकर्ता (User)</th>
                                                <th className="px-8 py-6 text-[10px] font-bold uppercase tracking-[0.2em] border-b border-gray-800">निवडलेली कामे (Selected Works)</th>
                                                <th className="px-8 py-6 text-[10px] font-bold uppercase tracking-[0.2em] border-b border-gray-800">पेमेंट तपशील (Payment)</th>
                                                <th className="px-8 py-6 text-[10px] font-bold uppercase tracking-[0.2em] border-b border-gray-800 text-center">स्थिती (Status)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {meetingAllocations.map((item) => (
                                                <tr key={item.id} className="hover:bg-blue-50/30 transition-colors group">
                                                    <td className="px-8 py-6">
                                                        <div className="flex items-center space-x-4">
                                                            <div className={`p-3 rounded-2xl ${item.type === 'Society' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                                                                {item.type === 'Society' ? <FaBuilding size={20} /> : <FaUser size={20} />}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-gray-900 text-lg group-hover:text-blue-600 transition-colors tracking-tight">{item.userName}</div>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">{item.type}</span>
                                                                    <span className="text-xs font-bold text-gray-400">{item.mobile}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <div className="flex flex-wrap gap-2">
                                                            {item.selectedWorks.map((work, idx) => (
                                                                <span key={idx} className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-xl font-bold text-xs border border-emerald-100 flex items-center gap-2">
                                                                    <FaClipboardList size={12} className="opacity-50" />
                                                                    {work}
                                                                </span>
                                                            ))}
                                                            {item.selectedWorks.length === 0 && (
                                                                <span className="text-gray-400 italic text-sm font-medium">No works selected</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <div className="space-y-1">
                                                            <div className="text-sm font-bold text-gray-800 font-mono">{item.receiptId}</div>
                                                            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                                {new Date(item.date).toLocaleDateString('en-GB')} {new Date(item.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-6 text-center">
                                                        <div className="flex items-center justify-center gap-2 text-green-600 font-bold text-xs bg-green-50 px-4 py-2 rounded-full border border-green-100 uppercase tracking-widest shadow-sm">
                                                            <FaCheckCircle className="animate-pulse" />
                                                            PAID
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ))}

                    {filteredData.length === 0 && (
                        <div className="p-20 text-center flex flex-col items-center justify-center bg-white rounded-[2.5rem] border border-gray-100 shadow-xl">
                            <div className="bg-gray-100 p-6 rounded-full text-gray-300 mb-4">
                                <FaSearch size={48} />
                            </div>
                            <p className="text-gray-400 font-black uppercase tracking-[0.2em]">कोणतीही नोंद आढळली नाही (No Records Found)</p>
                        </div>
                    )}
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerWorkAllocation;
